import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Instagram, MapPin, Send, Navigation } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, INSTAGRAM_URL, INSTAGRAM_HANDLE, ADDRESS, MAPS_EMBED_URL, MAPS_DIRECTIONS_URL } from "@/constants/site";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "", site_visit: false });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error(t.contact.toastRequired);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.contact.toastSuccess);
      setSubmitted(true);
      setForm({ name: "", phone: "", service: "", message: "", site_visit: false });
    } catch (err) {
      toast.error(t.contact.toastError);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#1A1412] border border-[#D4AF37]/25 text-[#FDFBF7] px-5 py-4 text-sm placeholder-[#B8A99A]/60 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300";

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#1A1412]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#D4AF37] tracking-[0.4em] uppercase text-sm mb-4">{t.contact.eyebrow}</p>
          <h2 className="font-heading text-4xl sm:text-5xl text-[#FDFBF7]" data-testid="contact-heading">
            {t.contact.heading}
          </h2>
          <p className="mt-6 text-[#B8A99A] leading-relaxed text-sm md:text-base max-w-md">
            {t.contact.intro}
          </p>

          <div className="mt-12 space-y-6">
            <a href={PHONE_TEL} className="flex items-center gap-4 group w-fit" data-testid="contact-phone-link">
              <span className="p-3 border border-[#D4AF37]/40 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#1A1412] transition-colors duration-300">
                <Phone className="h-5 w-5" />
              </span>
              <span className="text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors">{PHONE_DISPLAY}</span>
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group w-fit" data-testid="contact-instagram-link">
              <span className="p-3 border border-[#D4AF37]/40 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#1A1412] transition-colors duration-300">
                <Instagram className="h-5 w-5" />
              </span>
              <span className="text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors">{INSTAGRAM_HANDLE}</span>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <span className="p-3 border border-[#D4AF37]/40 text-[#D4AF37]">
                <MapPin className="h-5 w-5" />
              </span>
              <span className="text-[#FDFBF7] max-w-xs">{ADDRESS}</span>
            </div>

            <div className="pt-4">
              <iframe
                src={MAPS_EMBED_URL}
                title="The Build and Design office location — Indira Nagar, Lucknow"
                className="w-full h-64 border border-[#D4AF37]/25"
                loading="lazy"
                data-testid="office-map"
              />
              <a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 text-xs tracking-widest uppercase hover:bg-[#D4AF37] hover:text-[#1A1412] transition-colors duration-300"
                data-testid="get-directions-link"
              >
                <Navigation className="h-4 w-4" /> {t.contact.directions}
              </a>
            </div>
          </div>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#2A211D] border border-[#D4AF37]/20 p-8 lg:p-12 text-center"
            data-testid="enquiry-thank-you"
          >
            <p className="font-hindi text-4xl text-[#D4AF37]">{t.contact.thanksHindi}</p>
            <h3 className="font-heading text-3xl text-[#FDFBF7] mt-4">{t.contact.thanksTitle}</h3>
            <p className="mt-5 text-[#B8A99A] text-sm md:text-base leading-relaxed max-w-md mx-auto">
              {t.contact.thanksBody}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href={PHONE_TEL}
                className="flex items-center gap-2 bg-[#D4AF37] text-[#1A1412] px-6 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300"
                data-testid="thank-you-call-button"
              >
                <Phone className="h-4 w-4" /> {t.contact.thanksCall} {PHONE_DISPLAY}
              </a>
              <button
                onClick={() => setSubmitted(false)}
                className="border border-[#D4AF37]/60 text-[#D4AF37] px-6 py-3 text-xs tracking-widest uppercase hover:bg-[#D4AF37] hover:text-[#1A1412] transition-colors duration-300"
                data-testid="send-another-enquiry-button"
              >
                {t.contact.thanksAnother}
              </button>
            </div>
          </motion.div>
        ) : (
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="bg-[#2A211D] border border-[#D4AF37]/20 p-8 lg:p-12 space-y-6"
          data-testid="enquiry-form"
        >
          <div>
            <label htmlFor="enquiry-name" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.contact.nameLabel}</label>
            <input
              id="enquiry-name"
              data-testid="enquiry-name-input"
              className={inputCls}
              placeholder={t.contact.namePh}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="enquiry-phone" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.contact.phoneLabel}</label>
            <input
              id="enquiry-phone"
              data-testid="enquiry-phone-input"
              className={inputCls}
              placeholder={t.contact.phonePh}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="enquiry-service" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.contact.serviceLabel}</label>
            <select
              id="enquiry-service"
              data-testid="enquiry-service-select"
              className={inputCls}
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
            >
              <option value="">{t.contact.selectService}</option>
              {t.contact.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="enquiry-message" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.contact.messageLabel}</label>
            <textarea
              id="enquiry-message"
              data-testid="enquiry-message-input"
              rows={4}
              className={inputCls}
              placeholder={t.contact.messagePh}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="enquiry-site-visit-checkbox">
            <input
              type="checkbox"
              checked={form.site_visit}
              onChange={(e) => setForm({ ...form, site_visit: e.target.checked })}
              className="h-4 w-4 accent-[#D4AF37]"
            />
            <span className="text-sm text-[#B8A99A]">{t.contact.siteVisit}</span>
          </label>
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-button"
            className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1412] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? t.contact.sending : t.contact.submit}
            <Send className="h-4 w-4" />
          </button>
        </motion.form>
        )}
      </div>
    </section>
  );
};
